import { EarlyEtaStrategy } from '@bringg/types';
import { Attribute, Condition } from '../rule/rule-utils';
import type { NumericTranslations } from '../numeric';
import type { FreeTextTranslations } from '../free-text';
import type { BooleanTranslations } from '../boolean/boolean-condition';
import { ArrayTranslations } from '../array/array-condition';

export enum RadioValues {
	PRIORITY = 'priority',
	RATIO = 'ratio',
	FASTEST = 'fastest',
	CHEAPEST = 'cheapest'
}

export enum FallbackRadioOptions {
	FAST = 'fast',
	CHEAP = 'cheap'
}

export interface Option {
	key: string;
	value: string;
	ratio?: number;
	error?: boolean;
	disabled?: boolean;
}

export interface FleetRuleConditionsTranslations {
	numeric: NumericTranslations;
	array: ArrayTranslations;
	freeText: FreeTextTranslations;
	addAttribute: string;
	removeTooltipText?: string;

	getBooleanTranslations(attribute_name: string): BooleanTranslations;
}

export interface Settings {
	name: string;
	icon: string;
}

export interface FleetRuleRightSideTranslations {
	multiselectPlaceholder: string;
	assignmentMethod: string;
	assignBy: string;
	selectCarrier: string;
	fallback: string;
	tooltipText: string;
	totalPercentageLabel?: string;
	ratioTip?: string;
	ratioAllocation?: string;
	totalPercentageError?: string;
	dropdownTypeTitle?: string;
	dropdownTypeTooltip?: string;
}

export interface FleetRuleLeftSideTranslations extends FleetRuleConditionsTranslations {
	defineAttributes: string;
	setAsOverallDefault: string;
	multiselectPlaceholder: string;
	selectTeams: string;
	tooltipText: string;
}

export interface FleetRuleBodyTranslations {
	fleetRuleRightSide: FleetRuleRightSideTranslations;
	fleetRuleLeftSide: FleetRuleLeftSideTranslations;
}

export interface FleetRuleFooterTranslations {
	discard: string;
	save: string;
}
export interface FleetRuleTranslations {
	body: FleetRuleBodyTranslations;
	footer: FleetRuleFooterTranslations;
	inactive?: string;
	deleteTitle: string;
	deleteBody: string;
	cancel: string;
	delete: string;
}

export interface CommonFleetRuleProps {
	divider?: React.ReactNode;
	disabled?: boolean;
}

export interface FleetRuleHeaderHandlers {
	handleSettingsClick: (name: Settings['name']) => void;
	onNameChange: (name: string) => void;
}

export interface FleetRuleHeaderOptions {
	name: string;
	placeholder: string;
	settingsMenuItems: Settings[];
	index: number;
	isRuleEnabled?: boolean;
}

export interface FleetRuleRightSideHandlers {
	handleDragOption: (value: Option[]) => void;
	handleMethodChange?: (value: Option) => void;
	handleMethodDropdownChange?: (value: string[]) => void;
	handleFallbackChange: (value: Option) => void;
	handleMultiselectChange: (value: string[]) => void;
	onChangeCheckbox: (value: boolean) => void;
	handlePercentageInput?: (value: string) => void;
	handleRemoveDraggableOption?: (value: Option) => void;
}

export interface FleetRuleRightSideOptions {
	methodRadioOptions: Option[];
	methodDropdownTypeOptions?: Option[];
	fallbackRadioOptions: Option[];
	methodRadioValue?: RadioValues;
	methodDropdownTypeValue?: EarlyEtaStrategy;
	multiSelectOptions: Option[];
	draggableOptions: Option[];
	multiSelectDisabled?: boolean;
	fallbackRadioValue?: string;
	multiSelectValue?: string[];
	rightCheckboxValue?: boolean;
	shouldShowPercentageInput?: boolean;
	showPercentageTips?: boolean;
	showZeroPercentageError?: boolean;
	totalPercentage?: number;
	invalidPercents?: boolean;
	shouldShowRemoveDraggableOption?: boolean;
	markInvalidPercentage?: boolean;
	isDragDisabled?: boolean;
}

export interface FleetRuleLeftSideOptions {
	leftCheckboxValue?: boolean;
	attributes: Attribute[];
	conditions: Condition[];
	disabledMultiSelect?: boolean;
	multiSelectOptions: Option[];
	multiSelectValue?: string[];
	attributesDisabled?: boolean;
}

export interface FleetRuleLeftSideHandlers {
	onChangeCheckbox: (value: boolean) => void;
	handleMultiselectChange: (value: string[]) => void;
}

export interface FleetRuleFooterOptions {
	saveButtonDisabled: boolean;
	discardButtonDisabled: boolean;
}

export interface FleetRuleFooterHandlers {
	onDiscard: () => void;
	onSave: () => void;
}

export interface FeatureFlags {
	enableRatioPercentage?: boolean;
	pickupEtaAvailable?: boolean;
}

const allowedValuesForMultiselect = [RadioValues.PRIORITY, RadioValues.RATIO];

// show only if method is fastest, priority or ratio, but if method is fastest, show only if dropdown method is pickup
export const shouldShowRightSideMultiselect = (method: RadioValues, dropdownMethod: EarlyEtaStrategy) => {
	if (allowedValuesForMultiselect.indexOf(method) === -1) {
		return false;
	}

	return true;
};

export const shouldDisableDropdownOptions = ({
	methodRadioValue,
	methodDropdownTypeValue
}: {
	methodRadioValue: RadioValues;
	methodDropdownTypeValue: EarlyEtaStrategy;
}) => methodRadioValue === RadioValues.FASTEST && methodDropdownTypeValue === EarlyEtaStrategy.PICKUP;
